import { useEffect, useState } from "react";
import BodyCard from "../../../components/fundamentals/body-card/body-card";
import TableViewHeader from "../../../components/fundamentals/custom-table-header";
import Table from "../../../components/fundamentals/table";
import Button from "../../../components/fundamentals/button";
import Spinner from "../../../components/fundamentals/spinner";
import useNotification from "../../../hooks/use-notification";
import useImperativeDialog from "../../../hooks/use-imperative-dialog";
import mappingsService, { MappingCounts } from "../../../services/category-mappings.service";
import { CategoryMapping } from "../../../models/CategoryMapping";
import { Category } from "../../../models/Category";
import CategoryPicker from "./CategoryPicker";

const VIEWS = ["Correspondances de catégories"];

type Tab = "unmapped" | "auto" | "manual";

const TAB_LABELS: Record<Tab, string> = {
  unmapped: "Non rapprochées",
  auto: "Automatiques",
  manual: "Manuelles",
};

type PickerState =
  | { action: "map"; mapping: CategoryMapping }
  | { action: "merge"; mapping: CategoryMapping }
  | null;

/**
 * RAG-30 — Écran « Correspondances > Catégories ».
 * Onglets Non rapprochées / Automatiques / Manuelles, rapprochement manuel (fixe la
 * catégorie cible pour les prochains imports) et fusion d'une catégorie IT_* (déplace
 * les produits existants et désactive la branche).
 */
export default function CategoryMappingsPage() {
  const notify = useNotification();
  const confirm = useImperativeDialog();

  const [tab, setTab] = useState<Tab>("unmapped");
  const [mappings, setMappings] = useState<CategoryMapping[]>([]);
  const [counts, setCounts] = useState<MappingCounts>({ unmapped: 0, auto: 0, manual: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [picker, setPicker] = useState<PickerState>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  async function fetchList() {
    setLoading(true);
    setError(false);
    try {
      const result = await mappingsService.list(tab, "", page);
      setMappings(result.mappings);
      setCounts(result.counts);
      setLastPage(result.lastPage);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line
  }, [tab, page]);

  function changeTab(next: Tab) {
    setPage(1);
    setTab(next);
  }

  async function handleMap(mapping: CategoryMapping, category: Category) {
    setPicker(null);
    setBusyId(mapping.id);
    try {
      await mappingsService.update(mapping.id, category.id);
      notify("Correspondance enregistrée", `${mapping.externalLabel} → ${category.name}`, "success");
      await fetchList();
    } catch (e: any) {
      notify("Enregistrement refusé", e.message, e.status === 403 ? "warning" : "error");
    } finally {
      setBusyId(null);
    }
  }

  async function handleMerge(mapping: CategoryMapping, target: Category) {
    setPicker(null);
    if (!mapping.category_id) {
      return;
    }

    const confirmed = await confirm({
      heading: "Fusionner la catégorie",
      text: `Déplacer les ${mapping.products_count} produit(s) de « ${mapping.category?.name ?? mapping.category_id} » vers « ${target.name} », puis désactiver la catégorie d'origine ? Cette action modifie les produits existants.`,
      confirmText: "Oui, fusionner",
    });
    if (!confirmed) {
      return;
    }

    setBusyId(mapping.id);
    try {
      const moved = await mappingsService.merge(mapping.category_id, target.id);
      notify("Catégories fusionnées", `${moved} produit(s) déplacé(s) vers ${target.name}.`, "success");
      await fetchList();
    } catch (e: any) {
      notify("Fusion refusée", e.message, e.status === 403 ? "warning" : "error");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="flex flex-col grow h-full">
      <div className="w-full flex flex-col grow">
        <BodyCard customHeader={<TableViewHeader views={VIEWS} />} className="h-fit">
          <div className="flex flex-col gap-y-base">
            <p className="inter-small-regular text-grey-50">
              Uniformisez les catégories venant des différentes sources. Une correspondance
              «&nbsp;manuelle&nbsp;» s'applique aux prochains imports&nbsp;; la fusion déplace en plus les
              produits déjà rattachés à une catégorie créée automatiquement (IT_*).
            </p>

            {/* Onglets avec compteurs */}
            <div className="flex items-center gap-x-2">
              {(Object.keys(TAB_LABELS) as Tab[]).map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => changeTab(key)}
                  className={
                    "px-small h-10 rounded-rounded inter-small-semibold " +
                    (tab === key ? "bg-blue-60 text-grey-0" : "bg-grey-5 text-grey-70 hover:bg-grey-10")
                  }
                >
                  {TAB_LABELS[key]} ({counts[key]})
                </button>
              ))}
            </div>

            {error ? (
              <div className="py-large text-center inter-base-regular text-rose-50">
                Impossible de charger les correspondances.{" "}
                <button className="underline" onClick={fetchList}>
                  Réessayer
                </button>
              </div>
            ) : (
              <Table>
                <Table.Head>
                  <Table.HeadRow>
                    <Table.HeadCell>Source</Table.HeadCell>
                    <Table.HeadCell>Valeur externe</Table.HeadCell>
                    <Table.HeadCell>Catégorie cible</Table.HeadCell>
                    <Table.HeadCell>Produits</Table.HeadCell>
                    <Table.HeadCell>Origine</Table.HeadCell>
                    <Table.HeadCell></Table.HeadCell>
                  </Table.HeadRow>
                </Table.Head>
                <Table.Body>
                  {mappings.map((mapping) => (
                    <Table.Row key={mapping.id}>
                      <Table.Cell>{mapping.source}</Table.Cell>
                      <Table.Cell>{mapping.externalLabel}</Table.Cell>
                      <Table.Cell>{mapping.category?.name ?? "—"}</Table.Cell>
                      <Table.Cell>{mapping.products_count}</Table.Cell>
                      <Table.Cell>{mapping.origin === "manual" ? "Manuelle" : "Automatique"}</Table.Cell>
                      <Table.Cell>
                        <div className="flex items-center justify-end gap-x-2">
                          <Button
                            variant="secondary"
                            size="small"
                            loading={busyId === mapping.id}
                            onClick={() => setPicker({ action: "map", mapping })}
                          >
                            {mapping.category_id ? "Changer la cible" : "Rapprocher"}
                          </Button>
                          {mapping.isAutoCreatedCategory && (
                            <Button
                              variant="secondary"
                              size="small"
                              loading={busyId === mapping.id}
                              onClick={() => setPicker({ action: "merge", mapping })}
                            >
                              Fusionner
                            </Button>
                          )}
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            )}

            {loading && (
              <div className="py-large flex justify-center">
                <Spinner />
              </div>
            )}
            {!loading && !error && mappings.length === 0 && (
              <div className="py-large text-center text-grey-50 inter-base-regular">
                Aucune correspondance dans cet onglet.
              </div>
            )}

            {lastPage > 1 && (
              <div className="flex items-center justify-end gap-x-2">
                <Button variant="secondary" size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  Précédent
                </Button>
                <span className="inter-small-regular text-grey-50">
                  Page {page} / {lastPage}
                </span>
                <Button variant="secondary" size="small" disabled={page >= lastPage} onClick={() => setPage((p) => p + 1)}>
                  Suivant
                </Button>
              </div>
            )}
          </div>
        </BodyCard>
      </div>

      {picker && (
        <CategoryPicker
          title={picker.action === "merge" ? "Fusionner vers la catégorie…" : "Rapprocher vers la catégorie…"}
          onClose={() => setPicker(null)}
          onPick={(category) =>
            picker.action === "merge" ? handleMerge(picker.mapping, category) : handleMap(picker.mapping, category)
          }
        />
      )}
    </div>
  );
}
